import { computed, ref, watch } from 'vue';

// COMPONENTS
import { useLoader } from '@flash-global66/g-loader';
import type { GInputCode } from '@flash-global66/g-input-code';
import { openAlert } from '@flash-global66/g-dialog-alert';

// COMPOSABLES
import { useZIndex } from '@flash-global66/g-hooks';
import {
  useExpiredAuthContext,
  useHoldData,
  useCountdown,
  useRecaptcha,
} from '@/shared/common';
import {
  createRecaptchaFallbackError,
  isRecaptchaFallbackError,
  waitForRecaptchaContainer,
} from '@/shared/common/utils/recaptcha.util';
import { useOtpFlow } from '@/shared/otp/composables/useOtpFlow';

// CONSTANTS
import { alertCloseOtpTransaction } from '../../constants/alertsOptions.constant';
import {
  RECAPTCHA_V2_ABANDONED_MESSAGE,
  RECAPTCHA_V2_REQUIRED_MESSAGE,
} from '../../constants/otpInline.constant';
import {
  RECAPTCHA_FALLBACK_REASON,
} from '@/shared/common/';
import { LAST_CONNECTION_DATE_KEY } from '@/shared/common/constants/auth.constant';

// HELPERS
import { onButtonAlertCloseTransaction } from '../../utils/alertOtp.util';
import { getOtpPayload } from '../../utils/otpPayload.util';
import { isOtpThrottlingFailure } from '../../utils/otpThrottling.util';
import { getWebDeviceInfo } from '@/helpers/auth';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// STORE
import { useAccountStore } from '@/stores/account';

// API
// eslint-disable-next-line local-rules/architecture-boundaries -- deuda preexistente: el reenvío del código del login usa authenticationService de modules/authentication; la cubre BMB-4259
import { authenticationService } from '@/modules/authentication/services/authentication.service';
import { getReasonMessage } from '@/shared/common';
import type { ApiFailure } from '@/core/either';
import { otpAuthService } from '../../services/otpAuth.service';

// TYPES
// eslint-disable-next-line local-rules/architecture-boundaries -- brecha entre la rule y el linter: la regla no distingue `importKind: 'type'`, y acá el router llega inyectado por parámetro (`useOtp(emit, router)`), sin `useRouter()`. El propósito de la rule —que el composable no decida la navegación— ya está cumplido
import type { Router } from 'vue-router';
import type { IconString } from '@flash-global66/g-icon-font';
import type { TypeLogInData } from '@/types/Login';
import { ErrorsOtp } from '@/shared/common/errors';
import {
  BusinessFlow,
  RequestTypeEnum,
  OtpChannelEnum,
} from '../../types/otp.type';
import type {
  BusinessFlowType,
  ShippingOtp,
  OtpEmits,
} from '../../types/otp.type';

/**
 * Composable for OTP (One-Time Password) management and flow orchestration.
 * Handles OTP requests, validation, reCAPTCHA integration, and user interactions.
 *
 * @param emit - Event emitter for component communication (close events)
 * @param router - Vue Router instance for navigation handling
 * @returns Object containing OTP flow state, UI references, and action handlers
 */
export function useOtp(emit: OtpEmits, router: Router) {
  const { nextZIndex } = useZIndex();

  const otpFlow = useOtpFlow();
  const accountStore = useAccountStore();
  const holdData = useHoldData();
  const { notifyExpiredAuthContext } = useExpiredAuthContext();
  const inputCodeRef = ref<InstanceType<typeof GInputCode>>();
  const { showGLoader } = useLoader();
  const { executeRecaptchaFlow } = useRecaptcha();

  const otp = ref('');
  const isCompleteInputCode = ref(false);
  const isSubmitting = ref(false);

  const recaptchaV2ContainerId = 'recaptcha-otp-container';
  const isRecaptchaSectionVisible = ref(false);
  const isRecaptchaPending = ref(false);
  const didSendOtpSuccessfully = ref(false);
  const recaptchaSectionRef = ref<HTMLElement>();

  const FIELDS = 6;

  const zIndex = ref(100);

  const isLoadingOtp = ref(false);
  const hasFetchedWithButton = ref(false);
  const counter = 55;
  const INITIAL_COUNTER = 10;
  const requestCount = ref(0);
  const routesCloseOtp = new Set(['/transaction', '/exchange']);

  const {
    countdownMinutes,
    countdownSeconds,
    initCountdown,
    finishCountdown,
    isFinished,
  } = useCountdown();

  function startCountdown() {
    const duration = requestCount.value === 0 ? INITIAL_COUNTER : counter;
    requestCount.value++;
    initCountdown(duration);
  }

  /**
   * Muestra el error de un envío fallido y libera el contador: si el código no
   * salió, hacer esperar al usuario para pedirlo de nuevo no protege nada.
   * Con throttling lo mantiene: el backend rechazaría el pedido siguiente igual.
   */
  function showSendOtpError(message: string, failure: unknown): void {
    otpFlow.setOtpError(message);
    if (isOtpThrottlingFailure(failure)) return;
    finishCountdown();
  }

  const shippingTypeMap = ref({
    WHATSAPP: { text: 'Whatsapp', icon: 'brands whatsapp' as IconString },
    SMS: { text: 'SMS', icon: 'regular comment-alt' as IconString },
    EMAIL: { text: 'Email', icon: 'regular envelope' as IconString },
  });

  const shippingTypeInfo = computed(() => {
    return shippingTypeMap.value[otpFlow.channelType.value];
  });

  const shouldShowFirstButtons = computed(() => {
    return !hasFetchedWithButton.value && otpFlow.shouldShowFirstButtons.value;
  });

  // Los dos refs son un solo estado y siempre se mueven juntos. Separarlos deja
  // combinaciones sin significado: con pending en true y la sección oculta, el
  // input queda deshabilitado y el submit cortado sin nada visible que resolver.
  function requireRecaptchaChallenge(): void {
    isRecaptchaPending.value = true;
    isRecaptchaSectionVisible.value = true;
  }

  function clearRecaptchaChallenge(): void {
    isRecaptchaSectionVisible.value = false;
    isRecaptchaPending.value = false;
  }

  async function showV2Recaptcha(): Promise<void> {
    // El velo del GLoader es z-index 5000 y el popup 2001: sin apagarlo, el
    // widget se pinta debajo y su click es imposible. Y el showGLoader(false)
    // de los finally no llega, porque el await de arriba espera ese click.
    showGLoader(false);
    isLoadingOtp.value = false;
    requireRecaptchaChallenge();

    await waitForRecaptchaContainer();
    recaptchaSectionRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  function resumeLoadingAfterRecaptcha(): void {
    isRecaptchaPending.value = false;
    isLoadingOtp.value = true;
  }

  const isOtpInputBlocked = computed(() => isRecaptchaPending.value);

  function blockSubmitIfRecaptchaRequired(): boolean {
    if (didSendOtpSuccessfully.value) return false;

    const mustCompleteRecaptcha =
      isRecaptchaPending.value || isRecaptchaSectionVisible.value;
    if (!mustCompleteRecaptcha) return false;

    recaptchaSectionRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    return true;
  }

  function isChannelDisabled(channel: OtpChannelEnum): boolean {
    if (isRecaptchaSectionVisible.value) return true;
    if (otpFlow.error.value) return false;
    return isLoadingOtp.value && otpFlow.channelType.value !== channel;
  }

  function isChannelLoading(channel: OtpChannelEnum): boolean {
    return (
      isLoadingOtp.value &&
      !isRecaptchaSectionVisible.value &&
      otpFlow.channelType.value === channel
    );
  }

  function resetExpiredAuthContext(): void {
    holdData.clearObject();
    accountStore.lastConnectionDate = '';
    localStorage.removeItem(LAST_CONNECTION_DATE_KEY);
    otpFlow.hideOtp();
    notifyExpiredAuthContext();
  }

  async function requestOtpAuthLogin(
    type: ShippingOtp,
    loginFn: typeof authenticationService.login
  ) {
    const hasHeldData = holdData.checkData();

    if (!hasHeldData) {
      resetExpiredAuthContext();
      return;
    }

    const loginTemporal: TypeLogInData = holdData.getObject();

    try {
      showGLoader(true);
      startCountdown();
      isLoadingOtp.value = true;
      otpFlow.channelType.value = type;

      await executeRecaptchaFlow(
        BusinessFlow.SIGN_IN_B2B,
        async (token: string, version: 'V2' | 'V3') => {
          const fingerprintAgent = await FingerprintJS.load();
          const fingerprintResult = await fingerprintAgent.get();
          const deviceInfo = getWebDeviceInfo();

          const result = await loginFn(
            {
              email: loginTemporal.email,
              password: loginTemporal.password,
              authType: type,
              useOtp: true,
              recaptchaInfo: { token, version },
            },
            {
              fingerprint: fingerprintResult.visitorId,
              deviceInfo,
            }
          );

          return result.fold(
            (error) => {
              throw error;
            },
            (response) => {
              didSendOtpSuccessfully.value = true;
              clearRecaptchaChallenge();
              return response;
            }
          );
        },
        {
          containerId: recaptchaV2ContainerId,
          onV2Show: showV2Recaptcha,
          onV2Hide: resumeLoadingAfterRecaptcha,
        }
      );
    } catch (error: unknown) {
      const errorMap = ErrorsOtp;
      const failure = error as ApiFailure;

      const messageError = getReasonMessage({
        failure,
        defaultMessage: 'Error al validar. Vuelve a intentar',
        errorMap,
      });

      showSendOtpError(messageError, failure);
      clearRecaptchaChallenge();
    } finally {
      isLoadingOtp.value = false;
      showGLoader(false);
      otpFlow.shouldShowFirstButtons.value = false;
    }
  }

  async function postOtp(type: ShippingOtp) {
    try {
      isLoadingOtp.value = true;
      otpFlow.channelType.value = type;
      startCountdown();

      await executeRecaptchaFlow(
        otpFlow.businessFlow.value,
        async (token: string, version: 'V2' | 'V3') => {
          const { data, headers } = getOtpPayload(
            type,
            otpFlow.businessFlow.value,
            { token, version }
          );

          const result = await otpAuthService.createOtpCode(data, headers);

          if (result.isLeft()) {
            const error = result.value;
            if (
              error.reason === RECAPTCHA_FALLBACK_REASON &&
              isRecaptchaFallbackEnabled()
            ) {
              throw createRecaptchaFallbackError(error.reason);
            }
            await handleOtpError(error);
            return;
          }
          didSendOtpSuccessfully.value = true;
          clearRecaptchaChallenge();
        },
        {
          containerId: recaptchaV2ContainerId,
          onV2Show: showV2Recaptcha,
          onV2Hide: resumeLoadingAfterRecaptcha,
        }
      );
    } catch (error: unknown) {
      if (isRecaptchaRenderError(error)) {
        isLoadingOtp.value = false;
        requireRecaptchaChallenge();
        return;
      }

      const errorMap = ErrorsOtp;
      const failure = error as ApiFailure;

      const messageError = getReasonMessage({
        failure,
        defaultMessage: 'Error al enviar el código. Vuelve a intentar',
        errorMap,
      });

      showSendOtpError(messageError, failure);
      clearRecaptchaChallenge();
    } finally {
      inputCodeRef.value?.clearValues();
      isLoadingOtp.value = false;
      hasFetchedWithButton.value = true;
      otpFlow.shouldShowFirstButtons.value = false;
    }
  }

  function isRecaptchaFallbackEnabled(): boolean {
    const allowedBusinessFlows: BusinessFlowType[] = [
      BusinessFlow.REMITTANCE_B2B,
      BusinessFlow.EXCHANGE_B2B,
      BusinessFlow.MULTI_TRANSACTION_B2B,
      BusinessFlow.RFQ_REMITTANCE,
      BusinessFlow.BENEFICIARY_CREATE_B2B,
      BusinessFlow.BENEFICIARY_EDIT_B2B,
    ];
    return allowedBusinessFlows.includes(otpFlow.businessFlow.value);
  }

  /**
   * El popup monta el desafío V2 en su propia sección para los flujos de login
   * y para los que lo piden con `shouldMountRecaptcha`. El resto maneja su
   * recaptcha por fuera y el popup no interviene.
   */
  function shouldPopupHandleRecaptcha(): boolean {
    return (
      [RequestTypeEnum.AUTH, RequestTypeEnum.AUTH_V2].includes(
        otpFlow.requestType.value
      ) || otpFlow.shouldMountRecaptcha.value
    );
  }

  /**
   * La `action` que V3 valida es el businessFlow del propio flujo. Los de login
   * mantienen el suyo hardcodeado, que es lo que su backend espera.
   */
  function recaptchaBusinessFlow(): BusinessFlowType {
    return otpFlow.shouldMountRecaptcha.value
      ? otpFlow.businessFlow.value
      : BusinessFlow.SIGN_IN_B2B;
  }

  function isRecaptchaRenderError(error: unknown): boolean {
    return (
      error instanceof Error &&
      error.message.startsWith('[recaptcha]')
    );
  }

  async function handleOtpError(error: ApiFailure) {
    const messageError = getReasonMessage({
      failure: error,
      errorMap: ErrorsOtp,
      defaultMessage: 'Error al enviar el código. Vuelve a intentar',
    });

    showSendOtpError(messageError, error);
  }

  async function runOnRequest(type: ShippingOtp) {
    try {
      isLoadingOtp.value = true;
      otpFlow.channelType.value = type;
      startCountdown();

      if (!otpFlow.shouldMountRecaptcha.value) {
        await otpFlow.onRequest.value!(type);
        return;
      }

      await executeRecaptchaFlow(
        recaptchaBusinessFlow(),
        async (token: string, version: 'V2' | 'V3') => {
          const result = await otpFlow.onRequest.value!(type, {
            token,
            version,
          });
          // Sin estos resets el desafío queda "pendiente" para siempre y
          // blockSubmitIfRecaptchaRequired corta todo submit posterior: en 2FA
          // didSendOtpSuccessfully no se prende nunca.
          clearRecaptchaChallenge();
          return result;
        },
        {
          containerId: recaptchaV2ContainerId,
          onV2Show: showV2Recaptcha,
          onV2Hide: resumeLoadingAfterRecaptcha,
        }
      );
    } catch (error: unknown) {
      const messageError = isRecaptchaFallbackError(error)
        ? RECAPTCHA_V2_ABANDONED_MESSAGE
        : getReasonMessage({
            failure: error as ApiFailure,
            errorMap: ErrorsOtp,
            defaultMessage: 'Error al enviar el código. Vuelve a intentar',
          });
      showSendOtpError(messageError, error);
      clearRecaptchaChallenge();
    } finally {
      isLoadingOtp.value = false;
      hasFetchedWithButton.value = true;
      otpFlow.shouldShowFirstButtons.value = false;
    }
  }

  function requestOtp(type: ShippingOtp) {
    if (isRecaptchaPending.value) return;

    otpFlow.setOtpError('');
    otp.value = '';
    inputCodeRef.value?.clearValues();
    if (otpFlow.requestType.value === RequestTypeEnum.AUTH) {
      requestOtpAuthLogin(type, authenticationService.login);
    } else if (otpFlow.requestType.value === RequestTypeEnum.AUTH_V2) {
      requestOtpAuthLogin(type, authenticationService.loginV2);
    } else if (otpFlow.onRequest.value) {
      runOnRequest(type);
    } else {
      postOtp(type);
    }
  }

  async function validateAndSubmitOtp() {
    if (blockSubmitIfRecaptchaRequired()) return;
    if (isSubmitting.value) return;
    if (!otp.value?.length || otp.value.length !== FIELDS) return;

    // El velo va después de los guards: los dos returns están fuera del `try`,
    // así que prenderlo antes lo deja encendido sin que el `finally` lo apague.
    showGLoader(true);
    try {
      isSubmitting.value = true;
      if (shouldPopupHandleRecaptcha()) {
        await executeRecaptchaFlow(
          recaptchaBusinessFlow(),
          async (token: string, version: 'V2' | 'V3') => {
            const result = await otpFlow.onNext.value({
              otp: otp.value,
              type: otpFlow.channelType.value,
              recaptchaInfo: { token, version },
            });
            clearRecaptchaChallenge();
            return result;
          },
          {
            containerId: recaptchaV2ContainerId,
            onV2Show: showV2Recaptcha,
            onV2Hide: resumeLoadingAfterRecaptcha,
          }
        );
      } else {
        await otpFlow.onNext.value({
          otp: otp.value,
          type: otpFlow.channelType.value,
        });
      }
    } catch (error: unknown) {
      if (isRecaptchaRenderError(error)) {
        requireRecaptchaChallenge();
        return;
      }
      clearRecaptchaChallenge();
      // `onChange` es el único llamador y hace await sin catch: relanzar deja
      // el error sin handler y al usuario sin mensaje.
      if (
        otpFlow.shouldMountRecaptcha.value &&
        isRecaptchaFallbackError(error)
      ) {
        otpFlow.setOtpError(RECAPTCHA_V2_ABANDONED_MESSAGE);
        return;
      }
      throw error;
    } finally {
      inputCodeRef.value?.clearValues();
      isSubmitting.value = false;
      !otpFlow.hasLoaderHandler.value && showGLoader(false);
    }
  }

  function onComplete(value: boolean) {
    isCompleteInputCode.value = value;
  }

  async function onChange(value: string) {
    otpFlow.setOtpError('');
    otp.value = value;

    if (value.length === FIELDS) {
      await validateAndSubmitOtp();
    }
  }

  function onClose() {
    const isClose = checkCloseInRoute();
    if (isClose) {
      emit('close');
      otpFlow.hideOtp();
      clearRecaptchaChallenge();
      didSendOtpSuccessfully.value = false;
    }
  }

  function checkCloseInRoute(): boolean {
    if (
      routesCloseOtp.has(router?.currentRoute.value.path) &&
      otpFlow.isVisible.value
    ) {
      openAlert(alertCloseOtpTransaction).then(({ isPrimary, isSecondary }) => {
        onButtonAlertCloseTransaction({
          isPrimary,
          isSecondary,
          router,
        });
      });
      return false;
    }
    return true;
  }

  watch(
    () => router?.currentRoute.value.fullPath,
    () => {
      onClose();
    }
  );

  watch(
    () => otpFlow.isVisible.value,
    async (isShow) => {
      if (isShow) {
        requestCount.value = 0;
        zIndex.value = nextZIndex();
        didSendOtpSuccessfully.value = false;
        clearRecaptchaChallenge();

        const isUnverifiedPhoneLogin =
          otpFlow.channelType.value === OtpChannelEnum.EMAIL &&
          otpFlow.requestType.value === RequestTypeEnum.AUTH_V2;

        if (isUnverifiedPhoneLogin) {
          startCountdown();
          return;
        }
        if (shouldShowFirstButtons.value) {
          return;
        }
        if (
          [RequestTypeEnum.WITH_SESSION, RequestTypeEnum.AUTH_V2].includes(
            otpFlow.requestType.value
          )
        ) {
          requestOtp(otpFlow.channelType.value);
          return;
        }
        if (
          otpFlow.requestType.value === RequestTypeEnum.AUTH ||
          otpFlow.requestType.value === RequestTypeEnum['2FA']
        ) {
          startCountdown();
        }
      }
    },
    { immediate: true }
  );

  return {
    otpFlow,
    accountStore,
    zIndex,
    shouldShowFirstButtons,
    shippingTypeInfo,
    isFinished,
    isLoadingOtp,
    isChannelDisabled,
    isChannelLoading,
    shippingTypeMap,
    FIELDS,
    countdownMinutes,
    countdownSeconds,
    inputCodeRef,
    isRecaptchaSectionVisible,
    isRecaptchaPending,
    isOtpInputBlocked,
    recaptchaV2RequiredMessage: RECAPTCHA_V2_REQUIRED_MESSAGE,
    recaptchaSectionRef,
    recaptchaV2ContainerId,
    onChange,
    onComplete,
    requestOtp,
    onClose,
  };
}
